import { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FaEnvelope } from "react-icons/fa";
import "./styles.css";
import PhoneInput from "react-phone-number-input";
import ar from "react-phone-number-input/locale/ar";
import en from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import { UserContext } from "../../UserContext";
// Import Firebase auth and functions
import { auth } from "../../index";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendPasswordResetEmail,
} from "firebase/auth";
import OtpInput from "../PhoneVerification/OtpInput"; // Re-use the OtpInput component

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [resetMethod, setResetMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const labels = i18n.language === "ar" ? ar : en;
  const { setToken } = useContext(UserContext);

  // This function sets up and sends the OTP via Firebase
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!phoneNumber) {
      setError(t("Please enter a valid phone number."));
      return;
    }
    setIsLoading(true);
    try {
      const recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        }
      );
      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      setConfirmationResult(result);
      setOtpSent(true);
      setMessage(t("We have sent a verification code to your phone."));
    } catch (err) {
      console.error("Firebase OTP Error:", err);
      setError(t("Failed to send OTP. Please check the number or try again."));
    } finally {
      setIsLoading(false);
    }
  };

  // This function verifies the OTP and navigates to the next step
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError(t("Please enter the 6-digit code."));
      return;
    }
    setIsLoading(true);
    try {
      // confirmationResult.confirm() returns a UserCredential on success
      const userCredential = await confirmationResult.confirm(otpString);

      // Get the ID Token from the successfully signed-in user
      const idToken = await userCredential.user.getIdToken();

      setToken(idToken);
      // OTP is correct, now navigate to the reset password page.
      // Pass the token to the next component via navigation state.
      navigate("/reset-password", {
        state: { phoneNumber: phoneNumber },
      });
    } catch (err) {
      console.error("Firebase OTP Verification Error:", err);
      setError(t("Invalid OTP. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  // This function sends a password reset email via Firebase
  const handleSendEmailLink = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setMessage("");
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(
        t("a message was sent to your email. please check your inbox.")
      );
    } catch (err) {
      console.error("Firebase Email Reset Error:", err);
      if (err.code === "auth/user-not-found") {
        setError(t("No user found with this email address."));
      } else {
        setError(t("Failed to send reset email. Please try again later."));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMethodChange = (method) => {
    setResetMethod(method);
    setMessage("");
    setError("");
    setEmail("");
    setPhoneNumber("");
    setOtpSent(false);
  };

  // The main submit handler decides which flow to start
  const handleFormSubmit = (e) => {
    if (resetMethod === "email") {
      handleSendEmailLink(e);
    } else {
      // The phone flow is multi-step, handled by different buttons
      if (!otpSent) {
        handleSendOtp(e);
      } else {
        handleVerifyOtp(e);
      }
    }
  };

  return (
    <div className="auth-page-container">
      <div
        className={classNames("auth-container", {
          rtl: i18n.language === "ar",
        })}
      >
        <h1 className="auth-header">{t("Change Password")}</h1>

        <div className="login-method-tabs">
          <button
            className={classNames("tab-btn", {
              active: resetMethod === "email",
            })}
            onClick={() => handleMethodChange("email")}
          >
            {t("Email")}
          </button>
          <button
            className={classNames("tab-btn", {
              active: resetMethod === "phone",
            })}
            onClick={() => handleMethodChange("phone")}
          >
            {t("Phone Number")}
          </button>
        </div>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleFormSubmit} className="auth-form">
          {resetMethod === "email" ? (
            <>
              <p className="reset-subheader">
                {t("Enter your email address to receive a password reset link")}
              </p>
              <div className="input-wrapper">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("Email")}
                  className="signup-input-field"
                  required
                />
              </div>
              <button className="auth-btn" type="submit" disabled={isLoading}>
                {isLoading ? t("Sending...") : t("Send reset link")}
              </button>
            </>
          ) : (
            <>
              {!otpSent ? (
                <>
                  <p className="reset-subheader">
                    {t(
                      "Enter your phone number to receive a verification code"
                    )}
                  </p>
                  <div className="phone-input-wrapper">
                    <PhoneInput
                      international
                      placeholder={t("Phone Number")}
                      defaultCountry="EG"
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      countryCallingCodeEditable={false}
                      className="login-phone-input"
                      labels={labels}
                    />
                  </div>
                  <button
                    className="auth-btn"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? t("Sending...") : t("Send Code")}
                  </button>
                </>
              ) : (
                <>
                  <p className="otp-verification-subheader">
                    {t("Your code has been sent to you via SMS.")}
                  </p>
                  <OtpInput otp={otp} setOtp={setOtp} />
                  <button
                    className="auth-btn"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? t("Verifying...") : t("Verify & Proceed")}
                  </button>
                </>
              )}
            </>
          )}
        </form>

        <p className="auth-link-text">
          <Link to="/login">{t("Return to login")}</Link>
        </p>
        {/* Invisible div for Firebase Recaptcha */}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
};

export default ForgotPassword;
